<template>
    <div>
        <div class="white-box white-box-no-tab text-base xs:text-lg pb-10">
            <Title
                styleCodePage
                :text="`${$route.meta.pageTitle}`"
            />

            <h4 class="py-3 md:pb-3.5 md:pt-0">
                請輸入相關授權資訊
            </h4>
            <Skeletons v-if="isLoading" />
            <template v-else>
                <ul class="text-tip mb-2.5">
                    <li>僅限授權予要保人本人之信用卡</li>
                </ul>
                <AccountTable
                    class="hidden lg:block"
                    :data="data"
                >
                    <template #input>
                        <input
                            v-model.trim="data.cardNo1"
                            name="cardNo1"
                            type="text"
                            class="input-base w-1/6"
                            placeholder=""
                            autocomplete="off"
                            maxlength="4"
                            inputmode="numeric"
                            @input="validateBankAccount(); queryCardBankName(); moveToNextInput(true, 1); cleanBankInfo()"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo2"
                            name="cardNo2"
                            type="text"
                            class="input-base w-1/6"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount(); queryCardBankName(); moveToNextInput(true, 2); cleanBankInfo()"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo3"
                            name="cardNo3"
                            type="text"
                            class="input-base w-1/6"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount(); moveToNextInput(true, 3)"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo4"
                            name="cardNo4"
                            type="text"
                            class="input-base w-1/6"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount()"
                        >
                        <p class="error-msg">
                            {{ errorMessage }}
                        </p>
                    </template>
                    <template #select>
                        <FormList
                            :data="data"
                            :errorMessage="errorMessage"
                        />
                    </template>
                    <template #holder-id>
                        {{ holderId }}
                    </template>
                    <template #holder-name>
                        {{ holderName }}
                    </template>
                </AccountTable>
                <AccountList
                    class="lg:hidden"
                    :data="data"
                >
                    <template #input>
                        <input
                            v-model.trim="data.cardNo1"
                            name="cardNoL1"
                            type="text"
                            class="input-base w-[2.8rem] pl-0 pr-0"
                            placeholder=""
                            autocomplete="off"
                            maxlength="4"
                            inputmode="numeric"
                            @input="validateBankAccount(); queryCardBankName(); moveToNextInput(false, 1); cleanBankInfo()"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo2"
                            name="cardNoL2"
                            type="text"
                            class="input-base w-[2.8rem] pl-0 pr-0"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount(); queryCardBankName(); moveToNextInput(false, 2); cleanBankInfo()"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo3"
                            name="cardNoL3"
                            type="text"
                            class="input-base w-[2.8rem] pl-0 pr-0"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount(); moveToNextInput(false, 3)"
                        >
                        -
                        <input
                            v-model.trim="data.cardNo4"
                            name="cardNoL4"
                            type="text"
                            class="input-base w-[2.8rem] pl-0 pr-0"
                            placeholder=""
                            autocomplete="off"
                            inputmode="numeric"
                            maxlength="4"
                            @input="validateBankAccount()"
                        >
                        <p class="error-msg">
                            {{ errorMessage }}
                        </p>
                    </template>
                    <template #select>
                        <FormList
                            :data="data"
                            :errorMessage="errorMessage"
                            maxWidth="select-base xs:w-[calc(50%-0.5rem)] ml-0 mr-2 mb-2 max-w-[5rem]"
                        />
                    </template>
                    <template #holder-id>
                        {{ holderId }}
                    </template>
                    <template #holder-name>
                        {{ holderName }}
                    </template>
                </AccountList>
            </template>
        </div>
        <Bottombar>
            <template #right>
                <button
                    class="btn btn-accent rounded-full"
                    :disabled="disableGoNext()"
                    @click="goNext()"
                >
                    下一步
                </button>
            </template>
        </Bottombar>
        <Loading :isLoading="isLoading" />
    </div>
</template>

<script>
import { ref, computed, inject } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Title from '@/components/text/Title.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import Loading from '@/components/Loading.vue';
import AccountTable from './AccountTable.vue';
import AccountList from './AccountList.vue';
import routeName from '@/router/constant.js';
import Skeletons from './Skeletons.vue';
import { integer } from '@/utils/rules/customRules';
import dialogContnet from '@/json/dialogContent.js';
import codeList from '@/json/codeList.js';
import FormList from '@/views/aplCatlCreditCardAuth/creditCardInfo/Form.vue';
import { postCardBankName, submitCreditCartAuthInfo } from '@/apis/aplCatlCreditCardAuth';
import { openPopupCatchMessage } from '@/utils/vfmUtils';

export default {
    components: {
        FormList,
        AccountList,
        Title,
        Bottombar,
        Loading,
        AccountTable,
        Skeletons
    },
    setup() {
        const $vfm = inject('$vfm');
        const router = useRouter();
        const store = useStore();
        const isDisableButton = ref(false);
        const isLoading = ref(false);

        const data = computed(() => store.state.services.changeItemsResult[`${codeList.CREDIT_CARD_AUTH}cardInfo`]).value;
        const holderId = computed(() => store.state.permissions.userInfo.id).value;
        const holderName = computed(() => store.state.permissions.userInfo.name).value;
        const errorMessage = ref('');

        const modalShow = (params) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };
        const goNext = () => {
            if (errorMessage.value) {
                modalShow(dialogContnet.validateMsg);
                return;
            }

            if (data.bankCode === 'err') {
                modalShow({
                    title: '提示',
                    content: '您輸入信用卡卡號有誤，請再次確認！',
                    confirmButton: '確認',
                    cancelButton: null,
                    closeicon: true,
                });
                return;
            }

            isLoading.value = true;

            const requestData = {
                cardBankName: data.cardBankName,
                cardNo1: data.cardNo1,
                cardNo2: data.cardNo2,
                cardNo3: data.cardNo3,
                cardNo4: data.cardNo4,
                year: data.year,
                month: data.month,
                bankCode: data.bankCode,
            };
            submitCreditCartAuthInfo(requestData).then(() => {
                store.dispatch('services/setChangeItemsResult', { code: `${codeList.CREDIT_CARD_AUTH}cardInfo`, value: { ...data } });
                router.push({ name: routeName.CREDIT_CARD_AUTH_POLICY });
            }).catch(err => {
                openPopupCatchMessage(err);
            }).finally(() => {
                isLoading.value = false;
            });
        };
        // 驗證數字欄位
        const validateBankAccount = () => {
            errorMessage.value = integer(
                data.cardNo1 + data.cardNo2 + data.cardNo3 + data.cardNo4, '請輸入數字') || '';
        };

        const moveToNextInput = (isTable, index) => {
            const inputName = 'cardNo' + index;
            if (data[inputName].length === 4 && !integer(data[inputName])) {
                const nextInputName = (isTable) ? 'cardNo' + (index + 1) : 'cardNoL' + (index + 1);

                document.querySelector('input[name="' + nextInputName + '"]').focus();
            }
        };

        // 由 1-8 位卡號取得發卡機構
        const queryCardBankName = () => {
            if (!integer(data.cardNo1 + data.cardNo2) && data.cardNo1.length + data.cardNo2.length === 8) {
                const bankNameQueryData = {
                    cardNo1: data.cardNo1,
                    cardNo2: data.cardNo2,
                };
                postCardBankName(bankNameQueryData).then(res => {
                    const { cardBankName, bankCode } = res.data;
                    data.cardBankName = cardBankName;
                    data.bankCode = bankCode;
                }).catch(err => {
                    openPopupCatchMessage(err);
                });
            }
        };

        const cleanBankInfo = () => {
            if (data.cardNo1.length === 0 && data.cardNo2.length === 0) {
                data.cardBankName = '';
                data.bankCode = '';
            }
        };

        return {
            isDisableButton,
            isLoading,
            errorMessage,
            goNext,
            validateBankAccount,
            moveToNextInput,
            queryCardBankName,
            data,
            holderId,
            holderName,
            cleanBankInfo,
        };
    },
    methods: {
        disableGoNext() {
            const isNotLength12 = (this.data.cardNo1.length + this.data.cardNo2.length + this.data.cardNo3.length) !== 12;
            const isNotLargerThan3 = this.data.cardNo4.length < 3;
            const isMonthSelect = !!(this.data.month);
            const isYearSelect = !!(this.data.year);
            const bankCode = !!(this.data.bankCode);

            return !isMonthSelect || !isYearSelect || isNotLength12 || isNotLargerThan3 || !bankCode;
        }
    }
};
</script>
